
import { Baby } from "lucide-react";
import { format } from "date-fns";
import { BabyProfileDialog } from "@/components/BabyProfileDialog";
import { calculateAge } from "@/utils/ageCalculator";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { useEffect, useState } from "react";
import { getBabyProfile } from "@/utils/babyProfile";

interface BabyInfoSectionProps {
  babyProfile: any;
  onProfileUpdate: () => void;
}

export function BabyInfoSection({ babyProfile: initialBabyProfile, onProfileUpdate }: BabyInfoSectionProps) {
  const { language } = useLanguage();
  const [babyProfile, setBabyProfile] = useState(initialBabyProfile);

  useEffect(() => {
    // If the initial profile changes, update the local state
    setBabyProfile(initialBabyProfile);
  }, [initialBabyProfile]);

  const formatDate = (date: Date) => {
    if (language === 'ja') {
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    }
    return format(date, "MMMM d, yyyy");
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center">
        <Baby className="h-5 w-5 mr-2 text-primary" />
        {language === 'ja' ? '赤ちゃんの情報' : 'Baby Information'}
      </h3>
      
      {babyProfile ? (
        <div className="space-y-2">
          <div className="flex justify-between border-b pb-2">
            <span className="text-muted-foreground">{language === 'ja' ? '名前:' : 'Name:'}</span>
            <span className="font-medium">{babyProfile.name}</span>
          </div>
          
          {babyProfile.birthday && (
            <>
              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">{language === 'ja' ? '誕生日:' : 'Birthday:'}</span>
                <span className="font-medium">{formatDate(new Date(babyProfile.birthday))}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">{language === 'ja' ? '年齢:' : 'Age:'}</span>
                <span className="font-medium">{calculateAge(new Date(babyProfile.birthday), language)}</span>
              </div>
            </>
          )}
          
          {babyProfile.gender && (
            <div className="flex justify-between border-b pb-2">
              <span className="text-muted-foreground">{t('baby.gender', language)}:</span>
              <span className="font-medium">
                {babyProfile.gender === 'male' ? t('baby.gender.male', language) : 
                 babyProfile.gender === 'female' ? t('baby.gender.female', language) : 
                 t('baby.gender.other', language)}
              </span>
            </div>
          )}
          
          {babyProfile.originalHeight && (
            <div className="flex justify-between border-b pb-2">
              <span className="text-muted-foreground">{language === 'ja' ? '出生時の身長:' : 'Birth Height:'}</span>
              <span className="font-medium">{babyProfile.originalHeight}</span>
            </div>
          )}
          
          {babyProfile.originalWeight && (
            <div className="flex justify-between border-b pb-2">
              <span className="text-muted-foreground">{language === 'ja' ? '出生時の体重:' : 'Birth Weight:'}</span>
              <span className="font-medium">{babyProfile.originalWeight}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center p-4 border border-dashed rounded-lg">
          <p className="text-muted-foreground mb-2">
            {language === 'ja' ? '赤ちゃんのプロフィール情報はまだありません' : 'No baby profile information yet'}
          </p>
          <BabyProfileDialog onComplete={onProfileUpdate} />
        </div>
      )}
    </div>
  );
}
